package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPath
import co.maytes.standupbot.dashboard.styles.BrandHue
import co.maytes.standupbot.dashboard.styles.HomeArt
import co.maytes.standupbot.dashboard.styles.HomeClasses
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

private const val WIRE_WIDTH = "2"

@Composable
fun HomeFlowView(flow: HomeFlow) {
    HmDiv(HomeClasses.FLOW) {
        HmDiv(HomeClasses.FLOW_INPUTS) { flow.inputs.forEach { FlowNodeView(it, hub = false) } }
        HmDiv(HomeClasses.WIRE_CELL) { InputWires(flow) }
        FlowNodeView(flow.claude, hub = true)
        HmDiv(HomeClasses.WIRE_CELL, HomeClasses.WIRE_CELL_OUT) { OutputWire(flow) }
        FlowNodeView(flow.slack, hub = true)
    }
}

@Composable
private fun InputWires(flow: HomeFlow) {
    Svg(attrs = {
        classes(HomeClasses.WIRES)
        attr("viewBox", HomeArt.WIRE_BOX)
        attr("preserveAspectRatio", "none")
        attr("aria-hidden", "true")
    }) {
        flow.inputs.forEachIndexed { index, node ->
            val y = HomeArt.WIRE_SOURCE_Y[index]
            val curveX = HomeArt.WIRE_CURVE_X
            val target = HomeArt.WIRE_TARGET_Y
            Wire(
                path = "M0 $y C $curveX $y $curveX $target 100 $target",
                on = flow.inputWireOn(node),
                flowing = node.link.active && node.link != NodeLink.Unknown,
            )
        }
    }
}

@Composable
private fun OutputWire(flow: HomeFlow) {
    val known = flow.claude.link != NodeLink.Unknown
    Svg(attrs = {
        classes(HomeClasses.WIRES)
        attr("viewBox", HomeArt.WIRE_BOX)
        attr("preserveAspectRatio", "none")
        attr("aria-hidden", "true")
    }) {
        Wire(
            path = "M0 ${HomeArt.WIRE_TARGET_Y} L100 ${HomeArt.WIRE_TARGET_Y}",
            on = flow.outputWireOn,
            flowing = flow.outputWireOn && known,
        )
    }
}

@Composable
private fun Wire(path: String, on: Boolean, flowing: Boolean) {
    SvgPath(attrs = {
        classes(HomeClasses.WIRE, if (on) HomeClasses.WIRE_ON else HomeClasses.WIRE_OFF)
        attr("d", path)
        attr("stroke-width", WIRE_WIDTH)
    })
    if (flowing) {
        SvgPath(attrs = {
            classes(HomeClasses.WIRE, HomeClasses.WIRE_FLOW)
            attr("d", path)
            attr("stroke-width", WIRE_WIDTH)
        })
    }
}

@Composable
private fun FlowNodeView(node: FlowNode, hub: Boolean) {
    val id = node.id
    HmDiv(
        *classesOf(
            HomeClasses.NODE,
            if (hub) HomeClasses.NODE_HUB else null,
            if (node.link.active) HomeClasses.NODE_ON else HomeClasses.NODE_OFF,
        ),
        modifier = Modifier.tinted(tintOf(id)),
    ) {
        HmDiv(HomeClasses.CHIP) {
            Img(src = id.logo, attrs = Modifier.toAttrs { attr("alt", id.label) })
        }
        statusDotClass(node.link)?.let { cls -> HmDiv(HomeClasses.DOT, cls) {} }
        HmDiv(HomeClasses.LABEL) {
            SpanText(text = id.label)
            node.actionPath?.let { path ->
                HmLink(path, HomeClasses.CONNECT) { Text(node.link.actionLabel) }
            }
        }
    }
}

private fun statusDotClass(link: NodeLink): String? =
    when (link) {
        NodeLink.Connected -> HomeClasses.DOT_OK
        NodeLink.Degraded -> HomeClasses.DOT_WARN
        NodeLink.Broken -> HomeClasses.DOT_BAD
        NodeLink.Unknown, NodeLink.Off -> null
    }

fun tintOf(id: FlowNodeId): String =
    when (id) {
        FlowNodeId.Linear -> BrandHue.LINEAR
        FlowNodeId.Github -> BrandHue.GITHUB
        FlowNodeId.Calendar -> BrandHue.GOOGLE
        FlowNodeId.Claude -> BrandHue.CLAUDE
        FlowNodeId.Slack -> BrandHue.SLACK
    }
